import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import '@speles7172/message-console/styles.css';
import type { Quote } from '@peles/shared';
import { fetchRecordThreads, type RecordThread } from '../../api/notifications';
import { ConversationView } from '../messaging/ConversationView';
import { conversationLabel } from '../messaging/RecordConversations';

/** How many of the event's conversations are walked to find the candidates'. */
const PAGE = 50;
const MAX_PAGES = 6;

/**
 * The conversations of the vendors quoting a need, found among the event's.
 *
 * A request's letter files replies into that vendor's own conversation on the
 * event — the one the vendor email already uses — so a need has no thread of
 * its own: its conversations are the event's that carry one of its candidates
 * (`vendor:<id>`).
 */
export async function findVendorThreads(
  eventId: string,
  vendorIds: string[],
): Promise<{ thread: RecordThread; vendorId: string }[]> {
  const wanted = new Set(vendorIds.map((id) => `vendor:${id}`));
  const found: { thread: RecordThread; vendorId: string }[] = [];
  let offset = 0;
  for (let page = 0; page < MAX_PAGES; page++) {
    const result = await fetchRecordThreads('Event', eventId, { limit: PAGE, offset });
    for (const thread of result.items) {
      const match = thread.participants.find((p) => wanted.has(p.recipientId));
      if (match) found.push({ thread, vendorId: match.recipientId.slice('vendor:'.length) });
    }
    if (!result.hasMore) break;
    offset = result.offset + result.limit;
  }
  return found;
}

export function NeedConversations({ eventId, quotes }: { eventId: string; quotes: Quote[] }) {
  const vendorIds = [...new Set(quotes.map((q) => q.vendorId))].sort();
  const names = new Map(quotes.map((q) => [q.vendorId, q.vendorName]));
  const {
    data = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['need-conversations', eventId, vendorIds],
    queryFn: () => findVendorThreads(eventId, vendorIds),
    enabled: vendorIds.length > 0,
  });
  const [openId, setOpenId] = useState<string | null>(null);

  if (vendorIds.length === 0) {
    return (
      <p className="text-sm text-gray-500 py-6 text-center">
        Once vendors are asked for a quote, their replies are here.
      </p>
    );
  }
  if (isLoading) return <div className="h-10 rounded-lg bg-gray-100 animate-pulse" />;
  if (isError) {
    return (
      <p className="text-sm text-red-600">
        The conversations could not be loaded.{' '}
        <button
          type="button"
          className="underline min-h-11 sm:min-h-0"
          onClick={() => void refetch()}
        >
          Try again
        </button>
      </p>
    );
  }
  if (data.length === 0) {
    return (
      <p className="text-sm text-gray-500 py-6 text-center">
        No conversation with these vendors yet — replies to a request land here.
      </p>
    );
  }
  return (
    <div className="space-y-3">
      <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
        {data.map(({ thread, vendorId }) => (
          <li key={thread.id}>
            <button
              type="button"
              onClick={() => setOpenId((current) => (current === thread.id ? null : thread.id))}
              aria-expanded={openId === thread.id}
              className={`w-full text-left px-3 min-h-11 text-sm hover:bg-gray-50 ${
                openId === thread.id ? 'bg-primary-50 text-primary-700' : 'text-gray-800'
              }`}
            >
              <span className="font-medium">{names.get(vendorId) ?? 'Vendor'}</span>
              <span className="text-gray-500"> · {conversationLabel(thread)}</span>
            </button>
          </li>
        ))}
      </ul>
      {openId && (
        <div className="peles-controls">
          <ConversationView threadId={openId} record={{ entityType: 'Event', entityId: eventId }} />
        </div>
      )}
    </div>
  );
}
